import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { FLAG_TEXT, galText } from "./accounts";
import * as api from "./api";
import { SourceTag } from "./SourceTag";
import { ROUTE_STOP_COLUMNS, UNROUTED_COLUMNS } from "./columns";
import { weekText } from "./cycle";
import {
  canMove,
  collected,
  dayFromSearch,
  dayTitle,
  driverChoices,
  driverText,
  fitText,
  freePreferredTruck,
  lateText,
  memberText,
  routeChange,
  type RouteForm as ChangeForm,
  shiftDay,
  STATE_TEXT,
  stopLoadText,
  totalsText,
  truckError,
} from "./driverRoutes";
import { clockTime } from "./format";
import { go, useSection, useTitle } from "./hooks";
import { RouteCheckSummary } from "./RouteCheck";
import { RoutePlan } from "./RoutePlan";
import { fleetSize } from "./fleet";
import { saveFailure, SERVER_PROBLEM } from "./save";
import { byTruck, sortRows } from "./sort";
import { outcomeDetail } from "./today";
import { OffRouteMark } from "./OffRouteMark";
import { RequestedMark } from "./RequestedMark";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Hint } from "./Hint";
import { Skeleton } from "./Skeleton";

type DayLoad = api.RouteDay | "hidden" | "session" | "server" | "network" | undefined;
type Status = { ok: boolean; text: string; session?: boolean } | null;

const windowCell = (window: [string | null, string | null]) =>
  window[0] && window[1] ? `${window[0]}–${window[1]}` : "Any time";

function RouteForm({ found, onSave, busy }: { found: api.RouteDay; onSave: (entry: api.RouteEntry) => Promise<boolean>; busy: boolean }) {
  const [truck, setTruck] = useState("");
  const [size, setSize] = useState("");
  const [driver, setDriver] = useState("");
  const [error, setError] = useState<string | null>(null);
  const filled = useRef("");
  const fleetTrucks = byTruck(found.trucks ?? [], (option) => option.code);
  const chooseDriver = (id: string) => {
    setDriver(id);
    if (truck.trim() && truck !== filled.current) return;
    filled.current = freePreferredTruck(found.drivers, id, found.routes) ?? "";
    setTruck(filled.current);
  };
  const fromFleet = fleetSize(fleetTrucks, truck);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = truckError(truck) ?? (driver ? null : "Choose the driver.");
    setError(problem);
    if (problem) return;
    const entry = {
      id: crypto.randomUUID(),
      day: found.day,
      truck: truck.trim().toUpperCase(),
      size_gal: size ? Number(size) : fromFleet,
      driver_id: driver,
    };
    if (await onSave(entry)) {
      setTruck("");
      setSize("");
      filled.current = "";
    }
  };
  return (
    <form className="route-form" data-testid="route-form" onSubmit={(e) => void submit(e)} noValidate>
      <h2>Add a route</h2>
      <div className="fields">
        <label>
          Truck
          <input
            data-testid="route-truck"
            value={truck}
            maxLength={8}
            autoCapitalize="characters"
            list={fleetTrucks.length ? "route-trucks" : undefined}
            aria-invalid={error?.startsWith("Enter the truck") || undefined}
            onChange={(e) => setTruck(e.target.value)}
          />
          {fleetTrucks.length > 0 && (
            <datalist id="route-trucks" data-testid="route-trucks">
              {fleetTrucks.map((option) => (
                <option key={option.code} value={option.code}>
                  {`${galText(option.capacity_gal)} · ${option.depot}`}
                </option>
              ))}
            </datalist>
          )}
        </label>
        <label>
          Truck size
          <select data-testid="route-size" value={size} onChange={(e) => setSize(e.target.value)}>
            <option value="">{fromFleet === null ? "No size" : `From the truck (${galText(fromFleet)})`}</option>
            {found.truck_sizes.map((value) => (
              <option key={value} value={value}>
                {galText(value)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Driver
          <select data-testid="route-driver" value={driver} onChange={(e) => chooseDriver(e.target.value)} aria-invalid={error === "Choose the driver." || undefined}>
            <option value="">Choose…</option>
            {driverChoices(found.drivers, driver).map((person) => (
              <option key={person.id} value={person.id} data-email={person.email}>
                {memberText(person)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <button type="submit" className="primary" data-testid="route-add" disabled={busy}>
        Add the route
      </button>
    </form>
  );
}

function RouteChangeForm({
  route,
  found,
  busy,
  onSave,
  onClose,
}: {
  route: api.Route;
  found: api.RouteDay;
  busy: boolean;
  onSave: (change: api.RouteChange, failed: (text: string) => void) => Promise<boolean>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<ChangeForm>({ truck: route.truck, size: route.size_gal === null ? "" : String(route.size_gal), driver: route.driver_id });
  const [error, setError] = useState<string | null>(null);
  const fleetTrucks = found.trucks ?? [];
  const sizes = route.size_gal === null || found.truck_sizes.includes(route.size_gal) ? found.truck_sizes : [...found.truck_sizes, route.size_gal].sort((a, b) => a - b);
  const known = found.drivers.some((person) => person.id === route.driver_id);
  const fromFleet = fleetSize(fleetTrucks, form.truck);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = truckError(form.truck);
    setError(problem);
    if (problem) return;
    const change = routeChange(route, form, fleetTrucks);
    if (Object.keys(change).length === 0) {
      onClose();
      return;
    }
    if (await onSave(change, setError)) onClose();
  };
  return (
    <form className="inline-form" data-testid="route-change-form" onSubmit={(e) => void submit(e)} noValidate>
      <label>
        Truck
        <input
          data-testid="route-change-truck"
          value={form.truck}
          maxLength={8}
          autoCapitalize="characters"
          list={fleetTrucks.length ? `route-change-trucks-${route.id}` : undefined}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={error ? `route-change-error-${route.id}` : undefined}
          onChange={(e) => setForm((all) => ({ ...all, truck: e.target.value, size: e.target.value.trim().toUpperCase() === route.truck ? all.size : "" }))}
        />
        {fleetTrucks.length > 0 && (
          <datalist id={`route-change-trucks-${route.id}`}>
            {fleetTrucks.map((option) => (
              <option key={option.code} value={option.code}>
                {`${galText(option.capacity_gal)} · ${option.depot}`}
              </option>
            ))}
          </datalist>
        )}
      </label>
      <label>
        Truck size
        <select data-testid="route-change-size" value={form.size} onChange={(e) => setForm((all) => ({ ...all, size: e.target.value }))}>
          <option value="">{fromFleet === null ? "No size" : `From the truck (${galText(fromFleet)})`}</option>
          {sizes.map((value) => (
            <option key={value} value={String(value)}>
              {galText(value)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Driver
        <select data-testid="route-change-driver" value={form.driver} onChange={(e) => setForm((all) => ({ ...all, driver: e.target.value }))}>
          {!known && <option value={route.driver_id}>{driverText(route)}</option>}
          {driverChoices(found.drivers, route.driver_id).map((person) => (
            <option key={person.id} value={person.id}>
              {memberText(person)}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" className="primary" data-testid="route-change-save" disabled={busy}>
        Save the change
      </button>{" "}
      <button type="button" data-testid="route-change-close" onClick={onClose}>
        Keep it
      </button>
      {error && (
        <p className="error" role="alert" id={`route-change-error-${route.id}`}>
          {error}
        </p>
      )}
    </form>
  );
}

export function RoutesPage({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const [day, setDayState] = useState<string | undefined>(() => dayFromSearch(location.search));
  const [found, setFound] = useState<DayLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<Status>(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [changing, setChanging] = useState<string | null>(null);
  const [numbers, setNumbers] = useState<Record<string, string>>({});
  const [numberProblems, setNumberProblems] = useState<Record<string, string>>({});
  const refocus = useRef<{ stopId: string; up: boolean } | null>(null);
  const live = useRef(true);
  const [sort, setSort] = useSort("sort", ROUTE_STOP_COLUMNS);
  const [waitSort, setWaitSort] = useSort("waiting", UNROUTED_COLUMNS);
  useTitle(`Routes · ${org.name}`);
  useSection(location.hash, typeof found === "object" ? found.day : "");
  const setDay = (next: string) => {
    setDayState(next);
    const params = new URLSearchParams(location.search);
    params.set("day", next);
    history.replaceState(history.state, "", `${location.pathname}?${params.toString()}`);
  };
  useEffect(() => {
    if (canView !== true) return;
    live.current = true;
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.routeDay(token, day))
      .then((next) => live.current && setFound(next))
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live.current) setFound(code === 404 ? "hidden" : code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live.current = false;
    };
  }, [user, day, attempt, canView]);
  useEffect(() => {
    const target = refocus.current;
    if (busy || !target) return;
    refocus.current = null;
    const row = document.querySelector(`tr[data-stop-id="${target.stopId}"]`);
    const same = row?.querySelector<HTMLButtonElement>(`[data-testid="stop-${target.up ? "up" : "down"}"]`);
    const other = row?.querySelector<HTMLButtonElement>(`[data-testid="stop-${target.up ? "down" : "up"}"]`);
    (same && !same.disabled ? same : other)?.focus();
  }, [busy, found]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="routes-hidden">
        <h1 tabIndex={-1}>Routes</h1>
        <p>Your role does not show routes.</p>
      </main>
    );
  }
  const shown = typeof found === "object" ? found : undefined;
  const run = async (thing: string, action: (token: string) => Promise<api.RouteDay>, done: string, missing?: () => void, inline?: (text: string) => void) => {
    if (busy) return false;
    setBusy(true);
    setStatus(null);
    try {
      const next = await action(await user.getIdToken());
      if (!live.current) return false;
      setFound(next);
      setStatus({ ok: true, text: done });
      return true;
    } catch (e) {
      if (!live.current) return false;
      if (missing && e instanceof api.ApiError && e.status === 404) {
        missing();
        return false;
      }
      const failed = saveFailure(e, thing);
      if (inline && !failed.ok && !failed.session) {
        inline(failed.text);
        return false;
      }
      setStatus({ ok: false, text: failed.text, session: !failed.ok && failed.session });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const addStop = async (route: api.Route, stop: { account_id?: string; number?: string }, label: string) => {
    const saved = await run(
      "The stop",
      (token) => api.addRouteStop(token, route.id, { id: crypto.randomUUID(), ...stop }),
      `Saved: ${label} is on truck ${route.truck}.`,
      stop.number ? () => setNumberProblems((all) => ({ ...all, [route.id]: `No account has the number ${label}.` })) : undefined,
    );
    if (saved) setNumbers((all) => ({ ...all, [route.id]: "" }));
    if (stop.number) requestAnimationFrame(() => document.getElementById(`stop-number-${route.id}`)?.focus());
  };
  const planned = shown?.routes.filter((route) => route.status === "planned") ?? [];
  const move = (route: api.Route, stop: api.RouteStop, step: -1 | 1) => {
    refocus.current = { stopId: stop.id, up: step < 0 };
    void run("The order", (token) => api.moveRouteStop(token, route.id, stop.id, step), `Saved: ${stop.number} moved ${step < 0 ? "up" : "down"}.`);
  };
  const routeSection = (route: api.Route, index: number, all: api.Route[]) => {
    const editable = Boolean(shown?.can_plan) && route.status === "planned";
    const rows = sort ? sortRows(route.stops, ROUTE_STOP_COLUMNS, sort) : route.stops;
    const warning = fitText(route);
    return (
      <section
        key={route.id}
        className="route-card"
        id={all.findIndex((other) => other.truck === route.truck) === index ? `route-${route.truck}` : undefined}
        tabIndex={-1}
        aria-labelledby={`route-${route.id}`}
        data-testid="route"
        data-truck={route.truck}
        data-driver={route.driver_email ?? ""}
        data-status={route.status}
      >
        <h3 id={`route-${route.id}`}>
          Truck {route.truck} · <span className="clip name" title={route.driver_email ?? undefined}>{driverText(route)}</span>
          {route.status === "cancelled" && (
            <>
              {" "}
              <span className="chip-flag route-cancelled">Cancelled</span>
            </>
          )}
        </h3>
        <p className="sub" data-testid="route-totals">
          {totalsText(route)}
        </p>
        {route.started_at && (
          <p className="sub" data-testid="route-started">
            Started {clockTime(new Date(route.started_at), org.time_zone)}
          </p>
        )}
        <RouteCheckSummary route={route} />
        {warning && (
          <p className="error" data-testid="route-too-full">
            {warning}
          </p>
        )}
        {route.stops.length === 0 ? (
          <p data-testid="route-empty">No stops yet.</p>
        ) : (
          <div className="table-scroll" tabIndex={0} role="region" aria-label={`Stops of truck ${route.truck}`}>
            <table className="list route-stops" data-testid="route-stops" data-cols="route-stops-sort">
              <thead>
                <tr>
                  {ROUTE_STOP_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                  {editable && (
                    <th scope="col">
                      <span className="visually-hidden">Move or remove</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((stop, index) => (
                  <tr key={stop.id} data-testid="route-stop" data-stop-id={stop.id} data-number={stop.number} data-state={stop.state}>
                    <td className="num" data-label="Order">
                      {stop.position}
                    </td>
                    <td data-label="Arrive">{stop.arrive ?? "–"}</td>
                    <th scope="row">
                      <a href={`${base}/accounts/${stop.account_id}`} onClick={go} title={stop.address}>
                        <span className="account-number">{stop.number}</span> {stop.name}
                      </a>{" "}
                      <SourceTag tag={stop.data_source} />
                      <span className="sub desk-hide">{stop.address}</span>
                    </th>
                    <td data-label="Area">
                      {stop.area ?? "–"}
                      {stop.off_route && <> <OffRouteMark truck={route.truck} /></>}
                      {stop.requested && <> <RequestedMark /></>}
                    </td>
                    <td data-label="Window">{windowCell(stop.window)}</td>
                    <td className="num" data-label="Load">
                      <Hint text={collected(stop) ? "The gallons that the driver collected." : "The estimated gallons at this stop. It is not a measurement."} stop={false}>
                        {stopLoadText(stop)}
                      </Hint>
                    </td>
                    <td className="wrap route-state" data-label="State">
                      {STATE_TEXT[stop.state]}
                      {stop.late_min != null && (
                        <span className="chip-flag late-mark" data-testid="stop-late" data-late={stop.late_min}>
                          {lateText(stop.late_min)}
                        </span>
                      )}
                      {outcomeDetail(stop) && <span className="sub"> {outcomeDetail(stop)}</span>}
                    </td>
                    {editable && (
                      <td className="actions" data-label="">
                        <button
                          type="button"
                          data-testid="stop-up"
                          disabled={busy || Boolean(sort) || !canMove(index, rows.length, -1)}
                          aria-label={`Move ${stop.number} up`}
                          onClick={() => move(route, stop, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          data-testid="stop-down"
                          disabled={busy || Boolean(sort) || !canMove(index, rows.length, 1)}
                          aria-label={`Move ${stop.number} down`}
                          onClick={() => move(route, stop, 1)}
                        >
                          ↓
                        </button>
                        {stop.state === "planned" && (
                          <button
                            type="button"
                            data-testid="stop-remove"
                            disabled={busy}
                            aria-label={`Take ${stop.number} off truck ${route.truck}`}
                            onClick={() =>
                              void run("The change", (token) => api.removeRouteStop(token, route.id, stop.id), `Saved: ${stop.number} is off the route.`)
                            }
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {editable && (
          <div className="route-tools">
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                const number = (numbers[route.id] ?? "").trim().toUpperCase();
                if (number) void addStop(route, { number }, number);
              }}
            >
              <label>
                Add an account by number
                <input
                  id={`stop-number-${route.id}`}
                  data-testid="stop-number"
                  value={numbers[route.id] ?? ""}
                  maxLength={20}
                  autoCapitalize="characters"
                  aria-invalid={Boolean(numberProblems[route.id]) || undefined}
                  aria-describedby={numberProblems[route.id] ? `stop-number-error-${route.id}` : undefined}
                  onChange={(e) => {
                    setNumbers((all) => ({ ...all, [route.id]: e.target.value }));
                    setNumberProblems((all) => ({ ...all, [route.id]: "" }));
                  }}
                />
              </label>
              <button type="submit" data-testid="stop-add" disabled={busy || !(numbers[route.id] ?? "").trim()}>
                Add the stop
              </button>
              {numberProblems[route.id] && (
                <p className="error" role="alert" id={`stop-number-error-${route.id}`} data-testid="stop-number-error">
                  {numberProblems[route.id]}
                </p>
              )}
            </form>
            {changing === route.id && shown ? (
              <RouteChangeForm
                route={route}
                found={shown}
                busy={busy}
                onClose={() => setChanging(null)}
                onSave={(change, failed) => run("The change", (token) => api.changeRoute(token, route.id, change), `Saved: the route of truck ${change.truck ?? route.truck} changed.`, undefined, failed)}
              />
            ) : (
              <button type="button" data-testid="route-change" disabled={busy} onClick={() => setChanging(route.id)}>
                Change the truck or driver
              </button>
            )}
            {cancelling === route.id ? (
              <p className="confirm" data-testid="route-cancel-ask">
                Cancel truck {route.truck}? Its planned stops go back to the list of stops on no route.{" "}
                <button
                  type="button"
                  className="danger"
                  data-testid="route-cancel-confirm"
                  disabled={busy}
                  onClick={() => {
                    setCancelling(null);
                    void run("The cancel", (token) => api.changeRoute(token, route.id, { cancel: true }), `Saved: truck ${route.truck} is cancelled.`);
                  }}
                >
                  Cancel the route
                </button>{" "}
                <button type="button" onClick={() => setCancelling(null)}>
                  Keep it
                </button>
              </p>
            ) : (
              <button type="button" data-testid="route-cancel" disabled={busy} onClick={() => setCancelling(route.id)}>
                Cancel this route
              </button>
            )}
          </div>
        )}
      </section>
    );
  };
  return (
    <main className="issues-page routes-page wide" data-testid="routes-page" data-day={shown?.day ?? day ?? ""} data-today={shown?.today ?? ""}>
      <h1 tabIndex={-1}>Routes</h1>
      {shown && (
        <div className="tabs" role="group" aria-label="Day">
          <button type="button" data-testid="day-before" onClick={() => setDay(shiftDay(shown.day, -1))}>
            Day before
          </button>
          <button type="button" data-testid="day-today" aria-pressed={shown.day === shown.today} onClick={() => setDay(shown.today)}>
            Today
          </button>
          <button type="button" data-testid="day-after" onClick={() => setDay(shiftDay(shown.day, 1))}>
            Day after
          </button>
          <label className="day-pick">
            <span className="visually-hidden">Day</span>
            <input type="date" data-testid="day-input" value={shown.day} onChange={(e) => e.target.value && setDay(e.target.value)} />
          </label>
        </div>
      )}
      {found === undefined ? (
        <Skeleton />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => void signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <h2 data-testid="routes-day">{dayTitle(found.day, found.today)}</h2>
          {found.week !== null && found.week_start && (
            <p className="sub">
              {weekText({ week: found.week, week_start: found.week_start, week_end: shiftDay(found.week_start, 6) })}
            </p>
          )}
          {!found.can_plan && (
            <p data-testid="routes-view-only">
              {found.day < found.first_day || found.day > found.last_day
                ? "Routes change only from today to the end of the cycle board weeks."
                : "You can see the routes. A dispatcher plans them."}
            </p>
          )}
          <p role="status" data-testid="routes-status" className={status && !status.ok ? "error" : undefined}>
            {status?.text}
            {status?.session && (
              <>
                {" "}
                <button type="button" onClick={() => void signOut(auth)}>
                  Sign in again
                </button>
              </>
            )}
          </p>
          {found.can_see_plan && <RoutePlan key={found.day} base={base} user={user} found={found} timeZone={org.time_zone} onRoutes={setFound} />}
          {found.can_plan && (
            <RouteForm
              found={found}
              busy={busy}
              onSave={(entry) => run("The route", (token) => api.makeRoute(token, entry), `Saved: truck ${entry.truck} has a route.`)}
            />
          )}
          <section id="routes-of-the-day" tabIndex={-1} aria-labelledby="routes-list">
            <h2 id="routes-list">Routes of the day</h2>
            {found.routes.length === 0 ? (
              <p data-testid="routes-none">{found.can_plan ? "No routes yet. Add a route above." : "No routes on this day."}</p>
            ) : (
              <>
                <SortSelect id="route-stops-sort" columns={ROUTE_STOP_COLUMNS} sort={sort} onSort={setSort} fallback="In route order" />
                {sort && <p className="sub">To move stops, sort the stops in route order.</p>}
                {byTruck(found.routes, (route) => route.truck).map(routeSection)}
              </>
            )}
          </section>
          {found.can_plan && (
            <section aria-labelledby="routes-waiting" data-testid="routes-waiting">
              <h2 id="routes-waiting">Selected stops on no route</h2>
              <p className="sub">The stops of the cycle board for this week that no route of the week has.</p>
              {found.unrouted.length === 0 ? (
                <p data-testid="routes-waiting-none">Each selected stop of the week is on a route.</p>
              ) : (
                <>
                  <p className="actions">
                    <button
                      type="button"
                      data-testid="routes-fill"
                      disabled={busy || planned.length === 0}
                      onClick={() =>
                        void run("The routes", (token) => api.fillRoutes(token, found.day), `Saved: the stops are on the routes of ${dayTitle(found.day, found.today)}.`)
                      }
                    >
                      Fill the routes ({found.unrouted.length} stops)
                    </button>
                    {planned.length === 0 && <span className="sub"> Add a route first.</span>}
                  </p>
                  <SortSelect id="unrouted-sort" columns={UNROUTED_COLUMNS} sort={waitSort} onSort={setWaitSort} fallback="By area" />
                  <div className="table-scroll" tabIndex={0} role="region" aria-label="Selected stops on no route">
                    <table className="list unrouted" data-testid="unrouted" data-cols="unrouted-sort">
                      <thead>
                        <tr>
                          {UNROUTED_COLUMNS.map((column) => (
                            <SortTh key={column.key} column={column} sort={waitSort} onSort={setWaitSort} />
                          ))}
                          <th scope="col">Add to</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortRows(found.unrouted, UNROUTED_COLUMNS, waitSort).map((stop) => (
                          <tr key={stop.account_id} data-testid="unrouted-stop" data-number={stop.number}>
                            <th scope="row">
                              <a href={`${base}/accounts/${stop.account_id}`} onClick={go} title={stop.address}>
                                <span className="account-number">{stop.number}</span> {stop.name}
                              </a>
                              <span className="sub desk-hide">{stop.address}</span>
                            </th>
                            <td data-label="Area">{stop.area}</td>
                            <td data-label="Window">{windowCell(stop.window)}</td>
                            <td className="num" data-label="Estimated load">
                              {galText(stop.estimated_gal)}
                            </td>
                            <td className="flags" data-label="">
                              {stop.flags.map((flag) => (
                                <span key={flag} className={flag === "near_full" ? "chip-flag serious" : "chip-flag"}>
                                  {FLAG_TEXT[flag]}
                                </span>
                              ))}
                            </td>
                            <td data-label="Add to">
                              <select
                                className="unrouted-add"
                                data-testid="unrouted-add"
                                value=""
                                disabled={busy || planned.length === 0}
                                aria-label={`Add ${stop.number} to a route`}
                                onChange={(e) => {
                                  const route = planned.find((r) => r.id === e.target.value);
                                  if (route) void addStop(route, { account_id: stop.account_id }, stop.number);
                                }}
                              >
                                <option value="">Choose a truck…</option>
                                {planned.map((route) => (
                                  <option key={route.id} value={route.id}>
                                    Truck {route.truck} ({driverText(route)})
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}
    </main>
  );
}
